import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@medimo/ui";
import { confirmSignIn } from "aws-amplify/auth";
import { useForm } from "react-hook-form";
import { z } from "zod";

const SetInitialPasswordFormSchema = z
  .object({
    email: z.string(),
    password: z
      .string({
        required_error: "パスワードを入力してください",
      })
      .min(13, {
        message:
          "パスワードは13文字以上で、英大文字・英小文字・数字・記号をすべて含めてください。",
      })
      .regex(/[0-9]+/, { message: "数字を1文字以上使用してください" })
      .regex(/[a-z]+/, { message: "小文字を1文字以上使用してください" })
      .regex(/[A-Z]+/, { message: "大文字を1文字以上使用してください" })
      .regex(/[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]+/, {
        message:
          "記号（^ $ * . [ ] { } ( ) ? \" ! @ # % & / \\ , > < ' : ; | _ ~ ` = + -）を1文字以上使用してください",
      })
      .refine((val) => val === val.trim(), {
        message: "先頭または末尾にスペースは使用できません。",
      }),
    confirmPassword: z.string({
      required_error: "新しいパスワードを再入力してください",
    }),
    isTermsOfUseAgreed: z.boolean(),
  })
  .superRefine(({ password, confirmPassword, isTermsOfUseAgreed }, ctx) => {
    if (password !== confirmPassword) {
      ctx.addIssue({
        path: ["confirmPassword"],
        code: "custom",
        message: "パスワードが一致しません",
      });
    }
    if (!isTermsOfUseAgreed) {
      ctx.addIssue({
        path: ["isTermsOfUseAgreed"],
        code: "custom",
        message: "利用規約およびプライバシーポリシーに同意してください",
      });
    }
  });

export type SetInitialPasswordFormSchemaType = z.infer<
  typeof SetInitialPasswordFormSchema
>;

export const useSetInitialPasswordForm = ({
  onSetInitialPassword,
  onRequireTwoFactor,
  onSignInError,
  onSubmitOverride,
  defaultValues,
}: {
  onSetInitialPassword?: VoidFunction;
  onRequireTwoFactor?: VoidFunction;
  onSignInError?: VoidFunction;
  onSubmitOverride?: (
    values: SetInitialPasswordFormSchemaType,
  ) => Promise<void>;
  defaultValues: SetInitialPasswordFormSchemaType;
}) => {
  const { toast } = useToast();
  const form = useForm<SetInitialPasswordFormSchemaType>({
    defaultValues,
    mode: "onChange",
    resolver: zodResolver(SetInitialPasswordFormSchema),
  });

  const onSubmit = async (values: SetInitialPasswordFormSchemaType) => {
    // Callers that change the password elsewhere (e.g. the BE) must not touch the Amplify
    // session, and they own the success toast because only they know the outcome.
    if (onSubmitOverride) {
      await onSubmitOverride(values).catch((e: unknown) => {
        form.setError("root", {
          message:
            e instanceof Error && e.message
              ? e.message
              : "エラーが発生しました。もう一度お試しください。",
        });
      });
      return;
    }

    try {
      const result = await confirmSignIn({
        challengeResponse: values.password,
      });
      if (result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
        onRequireTwoFactor?.();
      } else {
        onSetInitialPassword?.();
      }
      toast({
        title: "パスワードを再設定しました",
      });
    } catch (e) {
      console.error(e);
      if (
        e instanceof Error &&
        e.message.includes("Invalid session for the user, session is expired.")
      ) {
        onSignInError?.();
        form.setError("root", {
          message:
            "セッションの有効期限が切れました。お手数ですが、再度ログインしてください。",
        });
      } else if (e instanceof Error && e.name === "SignInException") {
        onSignInError?.();
        form.setError("root", {
          message: "エラーが発生しました。もう一度ログインしてください。",
        });
      } else {
        form.setError("root", {
          message: "エラーが発生しました。もう一度お試しください。",
        });
      }
    }
  };

  return {
    form,
    onSubmit,
  };
};
